import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useMsal } from "@azure/msal-react";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

function FeatureCard({ icon, title, description }: FeatureProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        textAlign: "center",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        transition: "all 0.3s ease",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: 2,
        },
      }}
    >
      <Box sx={{ color: "primary.main", mb: 2, fontSize: 48 }}>
        {icon}
      </Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );
}

function LoggedOutLanding() {
  const { instance } = useMsal();
  const theme = useTheme();

  const handleLogin = async () => {
    try {
      await instance.loginPopup({
        scopes: ["User.Read"],
        prompt: "select_account",
      });
    } catch (err) {
      console.error("Login error:", err);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={4}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Stack spacing={3} sx={{ flex: 1 }}>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: "primary.dark",
                  lineHeight: 1.2,
                }}
              >
                Parsec Healthcare Management
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "text.secondary",
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Smart automation. Predictive insights. Seamless operations. Welcome to AI-powered healthcare management.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleLogin}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  fontWeight: 600,
                  alignSelf: "flex-start",
                  borderRadius: 1,
                  boxShadow: 3,
                  "&:hover": {
                    boxShadow: 4,
                  },
                }}
              >
                Sign In
              </Button>
            </Stack>
            <Box
              sx={{
                fontSize: 120,
                color: "primary.main",
                opacity: 0.1,
                textAlign: "center",
                flex: 1,
              }}
            >
              📋
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 8, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: "center",
              mb: 6,
              fontWeight: 700,
              color: "primary.dark",
            }}
          >
            Platform Capabilities
          </Typography>
          <Stack
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 3,
            }}
          >
            <FeatureCard
              icon={<SecurityIcon sx={{ fontSize: 48 }} />}
              title="Secure Access"
              description="Enterprise-grade authentication and authorization with Azure AD integration"
            />
            <FeatureCard
              icon={<SpeedIcon sx={{ fontSize: 48 }} />}
              title="Fast Processing"
              description="Real-time scheduling, encounter management, and financial processing"
            />
            <FeatureCard
              icon={<AnalyticsIcon sx={{ fontSize: 48 }} />}
              title="Analytics"
              description="Comprehensive insights into your healthcare operations performance and metrics"
            />
            <FeatureCard
              icon={<AutoAwesomeIcon sx={{ fontSize: 48 }} />}
              title="AI-Powered"
              description="Intelligent automation and machine learning to streamline workflows and boost efficiency"
            />
          </Stack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, bgcolor: "primary.main" }}>
        <Container maxWidth="lg">
          <Stack
            spacing={3}
            sx={{
              textAlign: "center",
              color: "white",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Ready to get started?
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Sign in to access your dashboard and start managing your healthcare operations
            </Typography>
            <Box>
              <Button
                variant="contained"
                color="inherit"
                size="large"
                onClick={handleLogin}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                  fontWeight: 600,
                  color: "primary.main",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                  },
                }}
              >
                Sign In Now
              </Button>
            </Box>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default LoggedOutLanding;
